import OpenSeadragon from "openseadragon";

export enum ScalebarType {
  MICROSCOPY = "microscopy",
  GEOLOGY = "geology",
}

export enum ScalebarLocation {
  BOTTOM_LEFT = "bottom-left",
  BOTTOM_RIGHT = "bottom-right",
  TOP_LEFT = "top-left",
  TOP_RIGHT = "top-right",
}

export interface ScalebarOptions {
  viewer: OpenSeadragon.Viewer;
  minWidth?: number;
  type?: ScalebarType;
  color?: string;
  fontColor?: string;
  backgroundColor?: string;
  fontSize?: string;
  fontFamily?: string;
  barThickness?: number;
  pixelsPerMeter?: number | null;
  referenceItemIdx?: number;
  location?: ScalebarLocation;
  xOffset?: number;
  yOffset?: number;
  stayInsideImage?: boolean;
  sizeAndTextRenderer?: (pixelsPerMeter: number) => { size: number; text: string };
}

// Missing TiledImage.viewportToImageZoom function in OSD 2.0.0
function tiledImageViewportToImageZoom(tiledImage: any, viewportZoom: any) {
  var ratio = tiledImage._scaleSpring.current.value *
          tiledImage.viewport._containerInnerSize.x /
          tiledImage.source.dimensions.x;
  return ratio * viewportZoom;
}

function getScalebarSizeAndText(ppm: any, minSize: any, unitSuffix: any, handlePlural: any, spacer: any) {
spacer = spacer === undefined ? ' ' : spacer;
  var value = normalize(ppm, minSize);
  var factor = roundSignificand(value / ppm * minSize, 3);
  var size = value * minSize;
  var plural = handlePlural && factor > 1 ? "s" : "";
  return {
      size: size,
      text: factor + spacer + unitSuffix + plural
  };
}

function getScalebarSizeAndTextForMetric(ppm: any, minSize: any, unitSuffix: any) {
  var value = normalize(ppm, minSize);
  var factor = roundSignificand(value / ppm * minSize, 3);
  var size = value * minSize;
  var valueWithUnit = getWithUnit(factor, unitSuffix);
  return {
      size: size,
      text: valueWithUnit
  };
}

function normalize(value : any, minSize: any) {
  var significand = getSignificand(value);
  var minSizeSign = getSignificand(minSize);
  var result = getSignificand(significand / minSizeSign);
  if (result >= 5) {
      result /= 5;
  }
  if (result >= 4) {
      result /= 4;
  }
  if (result >= 2) {
      result /= 2;
  }
  return result;
}

function getSignificand(x: any) {
  return x * Math.pow(10, Math.ceil(-log10(x)));
}

function roundSignificand(x: any, decimalPlaces: any) {
  var exponent = -Math.ceil(-log10(x));
  var power = decimalPlaces - exponent;
  var significand = x * Math.pow(10, power);
  // To avoid rounding problems, always work with integers
  if (power < 0) {
      return Math.round(significand) * Math.pow(10, -power);
  }
  return Math.round(significand) / Math.pow(10, power);
}

function log10(x: any) {
  return Math.log(x) / Math.log(10);
}

function getWithUnit(value: any, unitSuffix: any) {
  if (value < 0.000001) {
      return value * 1000000000 + " n" + unitSuffix;
  }
  if (value < 0.001) {
      return value * 1000000 + " μ" + unitSuffix;
  }
  if (value < 1) {
      return value * 1000 + " m" + unitSuffix;
  }
  if (value >= 1000) {
      return value / 1000 + " k" + unitSuffix;
  }
  return value + " " + unitSuffix;
}

function isDefined(variable: any) {
  return typeof (variable) !== "undefined";
}

export class Scalebar {
  private viewer: any;// OpenSeadragon.Viewer;
  private divElt: HTMLDivElement;
  private color: string;
  private fontColor: string;
  private backgroundColor: string;
  private fontSize: string;
  private fontFamily: string;
  private barThickness: number;
  private pixelsPerMeter: number | null;
  private referenceItemIdx: number;
  private location: ScalebarLocation;
  private xOffset: number;
  private yOffset: number;
  private stayInsideImage: boolean;
  private sizeAndTextRenderer: (pixelsPerMeter: number, minWidth: number) => { size: number; text: string };
  private minWidth: number; // Added missing property
  private options: ScalebarOptions;
  drawScalebar: (size: number, text: string) => void = () => {
    // Default implementation, can be overridden    
  }

  constructor(options: ScalebarOptions) {    
    if (!options.viewer) {
      throw new Error("A viewer must be specified.");
    }

    this.options = options;
    this.viewer = options.viewer;

    this.divElt = document.createElement("div");
    this.viewer.container.appendChild(this.divElt);
    this.divElt.style.position = "absolute";
    this.divElt.style.margin = "0";
    this.divElt.style.pointerEvents = "none";

    this.setMinWidth(options.minWidth || 150);

    this.setDrawScalebarFunction(options.type || ScalebarType.MICROSCOPY);
    this.color = options.color || "black";
    this.fontColor = options.fontColor || "black";
    this.backgroundColor = options.backgroundColor || "none";
    this.fontSize = options.fontSize || "";
    this.fontFamily = options.fontFamily || "";
    this.barThickness = options.barThickness || 2;
    this.pixelsPerMeter = options.pixelsPerMeter || null;
    this.referenceItemIdx = options.referenceItemIdx || 0;
    this.location = options.location || ScalebarLocation.BOTTOM_LEFT;
    this.xOffset = options.xOffset || 5;
    this.yOffset = options.yOffset || 5;
    this.stayInsideImage = options.stayInsideImage !== undefined ? options.stayInsideImage : true;
    this.sizeAndTextRenderer =
      options.sizeAndTextRenderer || ScalebarSizeAndTextRenderer.METRIC_LENGTH;
    this.minWidth = options.minWidth || 150; // Initialize minWidth
    this.drawScalebar = this.drawMicroscopyScalebar;

    this.viewer.addHandler("open", () => {
      this.refresh();
    });
    this.viewer.addHandler("animation", () => {
      this.refresh();
    });
    this.viewer.addHandler("resize", () => {
      this.refresh();
    });
  }

  setMinWidth(minWidth: number): void {
    this.minWidth = minWidth; // Update the minWidth property
    this.divElt.style.minWidth = minWidth + "px";
  }

  setDrawScalebarFunction(type: ScalebarType): void {
    // Logic to set the scalebar drawing function based on the type
    if (type === ScalebarType.MICROSCOPY) {
      this.sizeAndTextRenderer = ScalebarSizeAndTextRenderer.METRIC_LENGTH;
    } else if (type === ScalebarType.GEOLOGY) {
      this.sizeAndTextRenderer = ScalebarSizeAndTextRenderer.GEOLOGICAL_LENGTH;
    }
  }

 /**
 * Updates the options for the Scalebar instance.
 * @param options The new options to apply.
 */
private updateOptions(options: Partial<ScalebarOptions>): void {
  if (!options) {
    return;
  }

  // Update each option if it is defined
  if (isDefined(options.pixelsPerMeter)) {
    this.pixelsPerMeter = options.pixelsPerMeter!;
  }

  if (isDefined(options.minWidth)) {
    this.minWidth = options.minWidth!;
  }

  if (isDefined(options.location)) {
    this.location = options.location!;
  }

  if (isDefined(options.referenceItemIdx)) {
    this.referenceItemIdx = options.referenceItemIdx!;
  }

  if (isDefined(options.sizeAndTextRenderer)) {
    this.sizeAndTextRenderer = options.sizeAndTextRenderer!;
  }

  // Apply any additional updates to the scalebar's appearance
  if (this.divElt) {
    this.divElt.style.backgroundColor = options.backgroundColor || this.divElt.style.backgroundColor;
    this.divElt.style.color = options.fontColor || this.divElt.style.color;
    this.divElt.style.fontSize = options.fontSize || this.divElt.style.fontSize;
    this.divElt.style.fontFamily = options.fontFamily || this.divElt.style.fontFamily;
    //this.divElt.style.height = `${options.barThickness || parseInt(this.divElt.style.height || "4")}px`;
  }
}

 private refresh(): void {
   // Ensure the options are up-to-date
   this.updateOptions(this.options);   
   // Check if the viewer is open and all required properties are set
   if (
     !this.viewer.isOpen() ||
     !this.drawScalebar ||
     !this.pixelsPerMeter ||
     !this.location
   ) {
     this.divElt.style.display = "none";
     return;
   }
 
   // Make the scalebar visible
   this.divElt.style.display = "";
 
   // Get the viewport and the tiled image
   const viewport = this.viewer.viewport;
   const tiledImage = this.viewer.world.getItemAt(this.referenceItemIdx);
 
   if (!tiledImage) {
     console.error("Tiled image not found at the specified reference index.");
     this.divElt.style.display = "none";
     return;
   }
 
   // Calculate the current pixels per meter (PPM) based on the zoom level
   const zoom = tiledImageViewportToImageZoom(tiledImage, viewport.getZoom(true));
   const currentPPM = zoom * this.pixelsPerMeter;
 
   // Use the size and text renderer to calculate the scalebar properties
   const props = this.sizeAndTextRenderer(currentPPM, this.minWidth); // Use minWidth
 
   // Draw the scalebar with the calculated size and text
   this.drawScalebar(props.size, props.text);
 
   // Get the location of the scalebar and position it
   const location = this.getScalebarLocation();
   this.divElt.style.left = `${location.x}px`;
   this.divElt.style.top = `${location.y}px`;
  }
  
  private drawMicroscopyScalebar(size: any, text: any): void {  

    // this.divElt.style.border = "none";
    // this.divElt.style.borderBottom = this.barThickness + "px solid " + this.color;
     this.divElt.style.backgroundColor = this.backgroundColor;
     this.divElt.style.height = "14px";
     this.divElt.style.padding = "7px";     
     this.divElt.style.paddingTop = "18px";
     this.divElt.style.borderRadius = "3px";
    // this.divElt.innerHTML = "";
    // this.divElt.style.width = size + "px";


      const line = document.createElement("div");
      line.style.border = "none";
      line.style.borderBottom = this.barThickness + "px solid " + this.color;
      line.style.backgroundColor = this.backgroundColor;
      line.innerHTML = "";
      line.style.width = size + "px";
      line.style.position = "relative";
      line.style.bottom = `0px`; // Position above the scalebar with some spacing  


    const textElement = document.createElement("div");
    textElement.style.position = "absolute";
    textElement.style.bottom = `5px`; // Position above the scalebar with some spacing
    textElement.style.left = "50%";
    textElement.style.transform = "translateX(-50%)"; // Center the text horizontally
    textElement.style.fontSize = this.fontSize;
    textElement.style.fontFamily = this.fontFamily;
    textElement.style.color = this.fontColor;
    textElement.style.textAlign = "center";
    textElement.textContent = text;

    const background = document.createElement("div");
    background.style.position = "absolute";
    background.style.backgroundColor = this.color;

    
  // Create left rectangle
  const leftRect = document.createElement("div");
  leftRect.style.position = "absolute";
  leftRect.style.left = "5px";
  leftRect.style.bottom = `5px`; // Align with the bottom of the scalebar
  leftRect.style.width = `${this.barThickness}px`;
  leftRect.style.height = `${15}px`; // Height of the rectangle
  leftRect.style.backgroundColor = this.color;

  // Create right rectangle
  const rightRect = document.createElement("div");
  rightRect.style.position = "absolute";
  rightRect.style.right = "5px";
  rightRect.style.bottom = `5px`; // Align with the bottom of the scalebar
  rightRect.style.width = `${this.barThickness}px`;
  rightRect.style.height = `${15}px`; // Height of the rectangle
  rightRect.style.backgroundColor = this.color;

  // Append rectangles to the main scalebar element
  while (this.divElt.firstChild) {
    this.divElt.removeChild(this.divElt.firstChild);
  }
  this.divElt.appendChild(background);
  this.divElt.appendChild(line);
  this.divElt.appendChild(textElement);
  this.divElt.appendChild(leftRect);
  this.divElt.appendChild(rightRect);
}
private drawMapScalebar(size: any, text: any): void {
    this.divElt.style.fontSize = this.fontSize;
    this.divElt.style.fontFamily = this.fontFamily;
    this.divElt.style.textAlign = "center";
    this.divElt.style.color = this.fontColor;
    this.divElt.style.border = this.barThickness + "px solid " + this.color;
    this.divElt.style.borderTop = "none";
    this.divElt.style.backgroundColor = this.backgroundColor;
    this.divElt.innerHTML = text;
    this.divElt.style.width = size + "px";
}
/**
 * Compute the location of the scale bar.
 * @returns {OpenSeadragon.Point}
 */
private getScalebarLocation(): OpenSeadragon.Point  {
    if (this.location === ScalebarLocation.TOP_LEFT) {
        var x = 0;
        var y = 0;
        if (this.stayInsideImage) {
            var pixel = this.viewer.viewport.pixelFromPoint(
                    new OpenSeadragon.Point(0, 0), true);
            if (!this.viewer.wrapHorizontal) {
                x = Math.max(pixel.x, 0);
            }
            if (!this.viewer.wrapVertical) {
                y = Math.max(pixel.y, 0);
            }
        }
        return new OpenSeadragon.Point(x + this.xOffset, y + this.yOffset);
    }
    if (this.location === ScalebarLocation.TOP_RIGHT) {
        var barWidth = this.divElt.offsetWidth;
        var container = this.viewer.container;
        var x = container.offsetWidth - barWidth;
        var y = 0;
        if (this.stayInsideImage) {
            var pixel = this.viewer.viewport.pixelFromPoint(
                    new OpenSeadragon.Point(1, 0), true);
            if (!this.viewer.wrapHorizontal) {
                x = Math.min(x, pixel.x - barWidth);
            }
            if (!this.viewer.wrapVertical) {
                y = Math.max(y, pixel.y);
            }
        }
        return new OpenSeadragon.Point(x - this.xOffset, y + this.yOffset);
    }
    if (this.location === ScalebarLocation.BOTTOM_RIGHT) {
        var barWidth = this.divElt.offsetWidth;
        var barHeight = this.divElt.offsetHeight;
        var container = this.viewer.container;
        var x = container.offsetWidth - barWidth;
        var y = container.offsetHeight - barHeight;
        if (this.stayInsideImage) {
            var pixel = this.viewer.viewport.pixelFromPoint(
                    new OpenSeadragon.Point(1, 1 / this.viewer.source.aspectRatio),
                    true);
            if (!this.viewer.wrapHorizontal) {
                x = Math.min(x, pixel.x - barWidth);
            }
            if (!this.viewer.wrapVertical) {
                y = Math.min(y, pixel.y - barHeight);
            }
        }
        return new OpenSeadragon.Point(x - this.xOffset, y - this.yOffset);
    }
    if (this.location === ScalebarLocation.BOTTOM_LEFT) {
        var barHeight = this.divElt.offsetHeight;
        var container = this.viewer.container;
        var x = 0;
        var y = container.offsetHeight - barHeight;
        if (this.stayInsideImage) {
            var pixel = this.viewer.viewport.pixelFromPoint(
                    new OpenSeadragon.Point(0, 1 / this.viewer.source.aspectRatio),
                    true);
            if (!this.viewer.wrapHorizontal) {
                x = Math.max(x, pixel.x);
            }
            if (!this.viewer.wrapVertical) {
                y = Math.min(y, pixel.y - barHeight);
            }
        }
        return new OpenSeadragon.Point(x + this.xOffset, y - this.yOffset);
    }

    return new OpenSeadragon.Point(0, 0);
}
}

// Placeholder for the size and text renderer
export const ScalebarSizeAndTextRenderer = {
  METRIC_LENGTH: (pixelsPerMeter: number, minSize: any) => getScalebarSizeAndTextForMetric(pixelsPerMeter, minSize, "m"),
  GEOLOGICAL_LENGTH: (pixelsPerMeter: number, minSize: any) => ({
    size: 50 * pixelsPerMeter,
    text: `${(50 * pixelsPerMeter).toFixed(2)} m`,
  }),
};